import { addressLine } from '../utils/format';
import { useQuery } from '@tanstack/react-query';
import { endpoints } from '../api/endpoints';
import type { PrintHeader } from '../components/print/PrintDocument';

interface CompanyRow {
  legal_name: string;
  trade_name: string | null;
  gstin: string | null;
  pan: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  phone: string | null;
  email: string | null;
  print_tagline: string | null;
  logo_data_uri: string | null;
}

interface BranchRow {
  id: string;
  name: string;
  address: string | null;
  phone: string | null;
  gstin: string | null;
}

/**
 * The letterhead. A document without the shop's name and GSTIN on it is not a
 * document anyone can use, so this is loaded before anything is printed.
 */
export function usePrintHeader(branchName?: string): { header: PrintHeader; isLoading: boolean } {
  // The shop's own details, which hold the address, contact and tagline a bill
  // is headed with. Falls back to the companies list for name and logo.
  const company$ = useQuery({
    queryKey: ['company-details'],
    queryFn: () => endpoints.organisation.company(),
    staleTime: 600_000,
  });
  const companies = useQuery({
    queryKey: ['companies'],
    queryFn: () => endpoints.organisation.companies(),
    staleTime: 600_000,
  });
  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => endpoints.organisation.branches(),
    staleTime: 600_000,
  });

  const company = ((company$.data?.data as unknown as CompanyRow | null) ??
    (companies.data?.data ?? [])[0]) as CompanyRow | undefined;

  // The shop's full address, built from its parts, for the bill header.
  const companyAddress =
    [company?.address, [company?.city, company?.state, company?.pincode].filter(Boolean).join(' ')]
      .filter((p) => p && p.trim())
      .join(', ') || null;
  const branchList = (branches.data?.data ?? []) as unknown as BranchRow[];
  const branch = branchName ? branchList.find((b) => b.name === branchName) : branchList[0];

  return {
    isLoading: company$.isLoading || companies.isLoading || branches.isLoading,
    header: {
      companyName: company?.trade_name ?? company?.legal_name ?? '',
      companyGstin: branch?.gstin ?? company?.gstin ?? null,
      companyPan: company?.pan ?? null,
      companyAddress,
      companyPhone: company?.phone ?? null,
      companyEmail: company?.email ?? null,
      tagline: company?.print_tagline ?? null,
      branchName: branch?.name ?? branchName ?? '',
      branchAddress: addressLine(branch?.address) || companyAddress,
      branchPhone: branch?.phone ?? company?.phone ?? null,
      logoDataUri: company?.logo_data_uri ?? null,
    },
  };
}
